import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/shared/lib';

interface PagerProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Rows-per-page select; omitted when the caller keeps the size fixed. */
  pageSizes?: number[];
  onPageSizeChange?: (size: number) => void;
  /** Word for the counted items, e.g. "Einträgen". */
  unit?: string;
}

/**
 * Footer pager of the prototype's tables: "1–8 von 23" on the left, previous/next on the right,
 * with production's optional rows-per-page select.
 *
 * Nothing is rendered for an empty result (the table shows its own empty state), and the
 * prev/next pair is dropped when everything fits on one page — a permanently disabled pair of
 * buttons is noise rather than information.
 */
export function Pager({
  page,
  pageSize,
  total,
  onPageChange,
  pageSizes,
  onPageSizeChange,
  unit,
}: PagerProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();

  const pages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(page, pages);
  const from = (current - 1) * pageSize + 1;
  const to = Math.min(total, current * pageSize);
  const canPage = pages > 1;

  // An empty result needs no footer; the table above it already says so.
  if (total === 0) return null;

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '12px',
        px: '18px',
        py: '13px',
        background: theme.vars.palette.md.panel2,
        borderTop: `1px solid ${theme.vars.palette.md.border}`,
      })}
    >
      <Box
        component="span"
        sx={{
          fontSize: 12,
          color: 'text.secondary',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {`${fmt.number(from)}–${fmt.number(to)} ${t('pager.of')} ${fmt.number(total)}${unit ? ` ${unit}` : ''}`}
      </Box>

      {pageSizes && onPageSizeChange && (
        <TextField
          select
          size="small"
          fullWidth={false}
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          label={t('pager.rowsPerPage')}
          sx={{ minWidth: 118 }}
        >
          {pageSizes.map((size) => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </TextField>
      )}

      {canPage && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Box
            component="span"
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              mr: '4px',
            }}
          >
            {t('pager.page', { page: current, pages })}
          </Box>
          <Button
            size="small"
            onClick={() => onPageChange(current - 1)}
            disabled={current <= 1}
            sx={{ px: '14px' }}
          >
            {t('pager.previous')}
          </Button>
          <Button
            size="small"
            onClick={() => onPageChange(current + 1)}
            disabled={current >= pages}
            sx={{ px: '14px' }}
          >
            {t('pager.next')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
